import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import {materialClosureOutput} from "#material/nodes/base/material/validation/io"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {SubsurfaceFalloff} from "#material/nodes/shader/subsurface-scattering/types"
import {subsurfaceFalloffSchema} from "#material/nodes/shader/subsurface-scattering/types"

export interface SubsurfaceScatteringSockets extends MaterialNodeSockets {
    inputs: {
        anisotropy: "float"
        color: "color"
        ior: "float"
        normal: "normal"
        radius: "vector"
        roughness: "float"
        scale: "float"
    }
    settings: {
        anisotropy: "float"
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        ior: "float"
        normal: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        radius: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        roughness: "float"
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        sharpness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        textureBlur: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settingsExtensions: {
        falloff?: SubsurfaceFalloff
    }
    outputs: {
        bssrdf: "closure"
    }
}

// Blender enables these only for the random walk falloffs, and skin has no roughness to set
const RANDOM_WALK = {setting: "falloff", oneOf: ["RANDOM_WALK", "RANDOM_WALK_SKIN"]} as const
const RANDOM_WALK_WITH_ROUGHNESS = {setting: "falloff", oneOf: ["RANDOM_WALK"]} as const

export const subsurfaceScatteringDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeSubsurfaceScattering,
    },
    nodeClass: "SubsurfaceScattering",
    ui: {
        label: "Subsurface Scattering",
        color: "#2d8a5d",
        items: [
            {name: "bssrdf", label: "BSSRDF", valueType: "closure", output: true},
            {name: "color", label: "Color", valueType: "color", input: true, setting: true, defaultValue: {r: 0.8, g: 0.8, b: 0.8}},
            {name: "scale", label: "Scale", valueType: "float", input: true, setting: true, defaultValue: 0.05},
            {name: "radius", label: "Radius", valueType: "vector", input: true, setting: true, defaultValue: {x: 1, y: 0.2, z: 0.1}},
            {name: "ior", label: "IOR", valueType: "float", input: true, setting: true, defaultValue: 1.4, visibleWhen: RANDOM_WALK},
            {
                name: "roughness",
                label: "Roughness",
                valueType: "float",
                input: true,
                setting: true,
                defaultValue: 1,
                range: {min: 0, max: 1},
                visibleWhen: RANDOM_WALK_WITH_ROUGHNESS,
            },
            {name: "anisotropy", label: "Anisotropy", valueType: "float", input: true, setting: true, defaultValue: 0, visibleWhen: RANDOM_WALK},
            {name: "normal", label: "Normal", valueType: "vector", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {
                name: "falloff",
                defaultValue: "RANDOM_WALK",
                label: "Falloff",
                valueType: "string",
                options: [
                    {label: "Christensen-Burley", value: "BURLEY"},
                    {label: "Random Walk", value: "RANDOM_WALK"},
                    {label: "Random Walk (Skin)", value: "RANDOM_WALK_SKIN"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: {
                full: false,
                remark: "Approximated with a diffuse surface the scattering distance tints and thins; IOR, roughness and anisotropy have no counterpart.",
            },
        },
    },
    validation: {
        returns: z.strictObject({
            bssrdf: materialClosureOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                anisotropy: optionalMaterialInput,
                color: optionalMaterialInput,
                ior: optionalMaterialInput,
                normal: optionalMaterialInput,
                radius: optionalMaterialInput,
                roughness: optionalMaterialInput,
                scale: optionalMaterialInput,
            }),
            settings: z.strictObject({
                anisotropy: z.optional(z.number()),
                color: z.optional(color),
                falloff: z.optional(subsurfaceFalloffSchema),
                ior: z.optional(z.number()),
                normal: z.optional(vec3),
                radius: z.optional(vec3),
                roughness: z.optional(z.number()),
                scale: z.optional(z.number()),
                sharpness: z.optional(z.number()),
                textureBlur: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<SubsurfaceScatteringSockets>
